import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Container,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { HeaderBreadcrumbs, Iconify, Page, Scrollbar } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import { SkeletonLoadingTable } from '@/components/skeleton';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from '@/components/table';
import { PATH_USER } from '@/features/app';
import { getSettingsData } from '@/features/app/api';
import { useGetAccess } from '@/hooks/useAccess';
import useSettings from '@/hooks/useSettings';
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import {
  deleteServiceBlockedDate,
  getServiceBlockedDates,
  getVariants,
} from '../api';
import {
  BLOCKED_DATES_QUERY_KEY,
  BLOCKED_DATES_TABLE_HEAD,
  applySortFilterBlockedDates,
  getBlockedDateCityName,
  getBlockedDateServiceVariantName,
  getBlockedDateVariantName,
} from '../utils';
import { BlockedDateDialog, BlockedDatesTableRow } from '../components';
import { servicesManagementPath } from '../routes/paths';
import { ServiceBlockedDate, ServiceBlockedDateFilters } from '../types';

type FilterOption = {
  id: number | 'all';
  label: string;
};

export default function BlockedDatesList() {
  const { themeStretch } = useSettings();
  const allow = useGetAccess('services');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({
    defaultOrderBy: 'start_date',
    defaultOrder: 'asc',
    rowsPerPageStorageKey: 'services-blocked-dates-rows-per-page',
  });

  const [isBlockedDateDialogOpen, setIsBlockedDateDialogOpen] = useState(false);
  const [currentBlockedDateId, setCurrentBlockedDateId] = useState<
    string | undefined
  >();
  const [blockedDateToDelete, setBlockedDateToDelete] =
    useState<ServiceBlockedDate | null>(null);
  const [filterCityId, setFilterCityId] = useState<string>('all');
  const [filterVariantId, setFilterVariantId] = useState<string>('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const filters = useMemo<ServiceBlockedDateFilters>(
    () => ({
      city_id: filterCityId !== 'all' ? filterCityId : undefined,
      service_variant_id:
        filterVariantId !== 'all' ? filterVariantId : undefined,
      start_date: filterStartDate || undefined,
      end_date: filterEndDate || undefined,
    }),
    [filterCityId, filterEndDate, filterStartDate, filterVariantId]
  );

  const { data: settingsData, isLoading: isSettingsDataLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettingsData,
    onError: (error) =>
      errorMsg(error, 'Something went wrong while fetching settings data'),
    refetchOnWindowFocus: false,
  });

  const { data: serviceVariants, isLoading: isServiceVariantsLoading } =
    useQuery({
      queryKey: ['serviceVariants'],
      queryFn: getVariants,
      onError: (error) =>
        errorMsg(error, 'Something went wrong while fetching service variants'),
      refetchOnWindowFocus: false,
    });

  const { data: blockedDates, isLoading: isBlockedDatesLoading } = useQuery({
    queryKey: [BLOCKED_DATES_QUERY_KEY, filters],
    queryFn: () => getServiceBlockedDates(filters),
    onError: (error) =>
      errorMsg(error, 'Something went wrong while fetching blocked dates'),
  });

  const deleteBlockedDateMutation = useMutation({
    mutationFn: (blockedDate: ServiceBlockedDate) =>
      deleteServiceBlockedDate(String(blockedDate.id)),
    onSuccess: () => {
      queryClient.invalidateQueries([BLOCKED_DATES_QUERY_KEY]);
      successMsg('Blocked date successfully deleted');
      setBlockedDateToDelete(null);
    },
    onError: (error) =>
      errorMsg(error, 'Something went wrong while deleting blocked date'),
  });

  const citiesById = useMemo(
    () => new Map((settingsData?.cities || []).map((city) => [city.id, city])),
    [settingsData?.cities]
  );

  const serviceVariantsById = useMemo(
    () =>
      new Map((serviceVariants || []).map((variant) => [variant.id, variant])),
    [serviceVariants]
  );

  const cityFilterOptions = useMemo<FilterOption[]>(
    () => [
      { id: 'all', label: 'All cities' },
      ...(settingsData?.cities || []).map((city) => ({
        id: city.id,
        label: city.name,
      })),
    ],
    [settingsData?.cities]
  );

  const variantFilterOptions = useMemo<FilterOption[]>(
    () => [
      { id: 'all', label: 'All variants' },
      ...(serviceVariants || []).map((variant) => ({
        id: variant.id,
        label: `${
          variant.service?.name ?? 'N/A'
        } / ${getBlockedDateServiceVariantName(variant)}`,
      })),
    ],
    [serviceVariants]
  );

  const blockedDatesWithDisplayData = useMemo(
    () =>
      (blockedDates || []).map((blockedDate) => {
        const city = blockedDate.city_id
          ? citiesById.get(blockedDate.city_id)
          : null;
        const variant = blockedDate.service_variant_id
          ? serviceVariantsById.get(blockedDate.service_variant_id)
          : null;

        return {
          ...blockedDate,
          city: blockedDate.city ?? blockedDate.cities ?? city ?? null,
          service_variant: variant
            ? {
                ...blockedDate.service_variant,
                id: variant.id,
                name: variant.name,
                service_id: variant.service?.id,
                service: variant.service,
              }
            : blockedDate.service_variant ?? null,
        };
      }),
    [blockedDates, citiesById, serviceVariantsById]
  );

  const dataFiltered = applySortFilterBlockedDates({
    tableData: blockedDatesWithDisplayData,
    comparator: getComparator(order, orderBy),
  });

  const selectedCityFilter =
    cityFilterOptions.find((option) => String(option.id) === filterCityId) ??
    cityFilterOptions[0];
  const selectedVariantFilter =
    variantFilterOptions.find(
      (option) => String(option.id) === filterVariantId
    ) ?? variantFilterOptions[0];

  const handleOpenCreateDialog = () => {
    setCurrentBlockedDateId(undefined);
    setIsBlockedDateDialogOpen(true);
  };

  const handleOpenEditDialog = (blockedDateId: number) => {
    setCurrentBlockedDateId(String(blockedDateId));
    setIsBlockedDateDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setCurrentBlockedDateId(undefined);
    setIsBlockedDateDialogOpen(false);
  };

  const handleResetFilters = () => {
    setFilterCityId('all');
    setFilterVariantId('all');
    setFilterStartDate('');
    setFilterEndDate('');
    setPage(0);
  };

  const handleConfirmDelete = () => {
    if (!blockedDateToDelete) return;
    deleteBlockedDateMutation.mutate(blockedDateToDelete);
  };

  const denseHeight = dense ? 52 : 72;
  const isNotFound = !dataFiltered.length;
  const showSkeleton =
    isBlockedDatesLoading || isSettingsDataLoading || isServiceVariantsLoading;

  return (
    <Page title="Services: Blocked Dates">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Blocked Dates"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Services', href: servicesManagementPath('services') },
            { name: 'Blocked Dates' },
          ]}
          action={
            allow.canCreate && (
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={handleOpenCreateDialog}
              >
                Add blocked date
              </Button>
            )
          }
        />

        <Card>
          <Stack
            spacing={2}
            direction={{ xs: 'column', md: 'row' }}
            sx={{
              py: 2.5,
              px: 3,
              alignItems: { md: 'center' },
              flexWrap: 'wrap',
            }}
          >
            <Autocomplete
              size="small"
              value={selectedCityFilter}
              options={cityFilterOptions}
              disabled={isSettingsDataLoading}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, newValue) => {
                setFilterCityId(String(newValue?.id ?? 'all'));
                setPage(0);
              }}
              renderOption={(props, option) => (
                <MenuItem {...props} key={String(option.id)}>
                  {option.label}
                </MenuItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  sx={{ minWidth: { md: 200 } }}
                />
              )}
            />

            <Autocomplete
              size="small"
              value={selectedVariantFilter}
              options={variantFilterOptions}
              disabled={isServiceVariantsLoading}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, newValue) => {
                setFilterVariantId(String(newValue?.id ?? 'all'));
                setPage(0);
              }}
              renderOption={(props, option) => (
                <MenuItem {...props} key={String(option.id)}>
                  {option.label}
                </MenuItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Service variant"
                  sx={{ minWidth: { md: 260 } }}
                />
              )}
            />

            <TextField
              label="Start date"
              type="date"
              value={filterStartDate}
              onChange={(event) => {
                setFilterStartDate(event.target.value);
                setPage(0);
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { md: 160 } }}
            />

            <TextField
              label="End date"
              type="date"
              value={filterEndDate}
              onChange={(event) => {
                setFilterEndDate(event.target.value);
                setPage(0);
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { md: 160 } }}
            />

            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Iconify icon="eva:refresh-outline" />}
              onClick={handleResetFilters}
              sx={{ minWidth: { md: 110 } }}
            >
              Reset
            </Button>
          </Stack>

          {showSkeleton ? (
            <SkeletonLoadingTable />
          ) : (
            <>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 1100, position: 'relative' }}>
                  <Table size={dense ? 'small' : 'medium'}>
                    <TableHeadCustom
                      order={order}
                      orderBy={orderBy}
                      headLabel={BLOCKED_DATES_TABLE_HEAD}
                      rowCount={dataFiltered.length}
                      onSort={onSort}
                    />
                    <TableBody>
                      {dataFiltered
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((blockedDate) => (
                          <BlockedDatesTableRow
                            key={blockedDate.id}
                            row={blockedDate}
                            canUpdate={allow.canUpdate}
                            canDelete={allow.canDelete}
                            onEditRow={() =>
                              handleOpenEditDialog(blockedDate.id)
                            }
                            onDeleteRow={() =>
                              setBlockedDateToDelete(blockedDate)
                            }
                          />
                        ))}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(
                          page,
                          rowsPerPage,
                          dataFiltered.length
                        )}
                      />

                      <TableNoData
                        isNotFound={isNotFound}
                        title="No blocked dates available"
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <Box sx={{ position: 'relative' }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={dataFiltered.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={onChangePage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                />

                <FormControlLabel
                  control={<Switch checked={dense} onChange={onChangeDense} />}
                  label="Dense"
                  sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                />
              </Box>
            </>
          )}
        </Card>
      </Container>

      <BlockedDateDialog
        isOpen={isBlockedDateDialogOpen}
        onClose={handleCloseDialog}
        currentBlockedDateId={currentBlockedDateId}
      />

      <DialogSimple
        dialogTitle="Delete blocked date?"
        open={Boolean(blockedDateToDelete)}
        onClickButtonClose={() => setBlockedDateToDelete(null)}
        loadingButton
        loadingButtonText="Delete"
        loadingButtonColor="error"
        isSubmitting={deleteBlockedDateMutation.isLoading}
        onClickButtonLoading={handleConfirmDelete}
      >
        <Stack spacing={1.5} sx={{ px: 3, pb: 2 }}>
          <Typography variant="body2">
            This will permanently delete the selected blocked date.
          </Typography>
          <Typography variant="subtitle2">
            {blockedDateToDelete
              ? `${getBlockedDateCityName(
                  blockedDateToDelete
                )} / ${getBlockedDateVariantName(blockedDateToDelete)}`
              : ''}
          </Typography>
        </Stack>
      </DialogSimple>
    </Page>
  );
}
